import React, { useMemo, useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { WEEKS_IN_YEAR } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import { springConfig } from '../../utils/animations';

interface WeeksGridProps {
  stats: LifeStats;
}

const WeeksGrid: React.FC<WeeksGridProps> = ({ stats }) => {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);

  // Track if we've done the initial animation
  useEffect(() => {
    if (!hasAnimated) {
      const timer = setTimeout(() => setHasAnimated(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [hasAnimated]);

  // Memoize grid data to prevent recalculation on every render
  const gridData = useMemo(() => {
    if (stats.lifeExpectancy <= 0) return null;

    const totalWeeks = Math.ceil(stats.lifeExpectancy * WEEKS_IN_YEAR);
    const rows = Math.ceil(stats.lifeExpectancy);
    const grid = [];

    for (let row = 0; row < rows; row++) {
      const yearWeeks = [];
      for (let col = 0; col < WEEKS_IN_YEAR; col++) {
        const weekNumber = row * WEEKS_IN_YEAR + col;
        if (weekNumber >= totalWeeks) break;

        const isPast = weekNumber < stats.weeksLived;
        const isCurrent = weekNumber === Math.floor(stats.weeksLived);

        // Different color based on progress through expected life
        let bgColor = "bg-gray-100 dark:bg-catppuccin-surface1";
        let darkBgColor = "dark:bg-catppuccin-surface1";

        if (isPast) {
          const progress = weekNumber / totalWeeks;
          if (progress < 0.25) {
            bgColor = "bg-green-200";
            darkBgColor = "dark:bg-green-700";
          } else if (progress < 0.5) {
            bgColor = "bg-yellow-200";
            darkBgColor = "dark:bg-yellow-700";
          } else if (progress < 0.75) {
            bgColor = "bg-orange-200";
            darkBgColor = "dark:bg-orange-700";
          } else {
            bgColor = "bg-red-200";
            darkBgColor = "dark:bg-red-700";
          }
        }

        yearWeeks.push({
          weekNumber,
          isPast,
          isCurrent,
          bgColor: isCurrent ? "bg-blue-500 dark:bg-catppuccin-blue" : `${bgColor} ${darkBgColor}`,
          row,
          col
        });
      }

      if (yearWeeks.length > 0) {
        grid.push({
          year: row,
          weeks: yearWeeks
        });
      }
    }

    return grid;
  }, [stats.lifeExpectancy, stats.weeksLived]);

  if (!gridData) return null;

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.02,
        delayChildren: 0.1
      }
    }
  };

  // Row animation variants
  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.gentle
    }
  };

  // Cell animation variants for initial load
  const cellVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: springConfig.bouncy
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <div className="space-y-1 max-h-[600px] overflow-y-auto pr-4 pb-4 custom-scrollbar">
        {gridData.map((yearData) => (
          <div key={`year-${yearData.year}`} className="flex items-center">
            <div className="w-10 text-xs text-gray-500 dark:text-catppuccin-subtext0 pr-2 text-right font-medium">
              {yearData.year + 1}
            </div>
            <div className="flex flex-wrap">
              {yearData.weeks.map((week) => (
                <div
                  key={`week-${week.weekNumber}`}
                  className={`w-3 h-3 ${week.bgColor} m-px rounded-sm transition-all duration-300 hover:brightness-125 hover:ring-1 hover:ring-gray-400 dark:hover:ring-catppuccin-overlay0`}
                  title={`Week ${week.weekNumber + 1} (Year ${week.row + 1})`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-1 max-h-[600px] overflow-y-auto pr-4 pb-4 custom-scrollbar"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {gridData.map((yearData, yearIndex) => (
        <motion.div
          key={`year-${yearData.year}`}
          className="flex items-center"
          variants={rowVariants}
          custom={yearIndex}
        >
          <motion.div
            className="w-10 text-xs text-gray-500 dark:text-catppuccin-subtext0 pr-2 text-right font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: yearIndex * 0.02 }}
          >
            {yearData.year + 1}
          </motion.div>
          <div className="flex flex-wrap">
            {yearData.weeks.map((week, weekIndex) => (
              <motion.div
                key={`week-${week.weekNumber}`}
                className={`w-3 h-3 ${week.bgColor} m-px rounded-sm cursor-pointer hover:brightness-125 hover:ring-1 hover:ring-gray-400 dark:hover:ring-catppuccin-overlay0 ${week.isCurrent ? 'ring-2 ring-blue-400 dark:ring-catppuccin-blue animate-pulse' : ''}`}
                variants={hasAnimated ? undefined : cellVariants}
                initial={hasAnimated ? undefined : "hidden"}
                animate={hasAnimated ? undefined : "visible"}
                transition={{
                  delay: hasAnimated ? 0 : (yearIndex * 0.02 + weekIndex * 0.001),
                  ...springConfig.snappy
                }}
                title={`Week ${week.weekNumber + 1} (Year ${week.row + 1})`}
              />
            ))}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default WeeksGrid;
